import { ApolloError, useQuery } from '@apollo/client'
import * as R from 'ramda'
import { useContext, useMemo } from 'react'

import { GetCustomerAddressesDocument, GetCustomerAddressesQuery } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { AddressRestrictions } from '../types'

const useSortedCustomerAddresses = (
  addressRestrictions?: AddressRestrictions,
): {
  addresses: NonNullable<GetCustomerAddressesQuery['customer']>['addresses']
  error: ApolloError | undefined
  loading: boolean
} => {
  const token = useContext(TokenContext)
  const { data, loading, error } = useQuery(GetCustomerAddressesDocument, {
    context: { token },
  })
  useLogAndCaptureError(error)

  const addresses = useMemo(() => {
    const unsorted = data?.customer?.addresses
    if (!unsorted) {
      return undefined
    }
    const filterAddress = addressRestrictions
      ? R.filter(addressRestrictions.isAddressAllowed)
      : R.identity

    return filterAddress(unsorted.slice()).sort((a, b) => {
      if (a && (a.default_billing || a.default_shipping)) return -1
      if (b && (b.default_billing || b.default_shipping)) return 1
      if (a && !b) return -1
      if (!a && b) return 1
      if (!(a && b)) return 0
      return `${a.firstname || ''}${a.lastname || ''}`.localeCompare(
        `${b.firstname || ''}${b.lastname || ''}`,
      )
    })
  }, [addressRestrictions, data?.customer?.addresses])
  return { addresses, loading, error }
}

export default useSortedCustomerAddresses
